/**
 * Shared status indicator dot — used by the Space List view (per-row
 * status pip + status picker), the Team Tasks page, the Task Monitor
 * page, and any other list that renders a status circle next to a
 * task name. Centralising the rendering here keeps every surface
 * visually consistent: the ClickUp-style dashed ring for pre-work
 * statuses, and the bullseye/target pattern for in-motion statuses.
 *
 * Rendered as SVG so the dashed ring stays crisp at small sizes —
 * CSS `border-style: dashed` at 14px draws too few dashes to read,
 * and the in-motion bullseye needs precise control over the inner
 * disc + outer halo geometry.
 */

/**
 * Decide whether a status counts as "pre-work" (dashed-ring) or
 * "in motion / done" (filled bullseye).
 *
 * Primary signal: `status.category` — the default seed sets this to
 * `not_started` / `backlog` for pre-work columns. If that's set, we
 * use it directly.
 *
 * Fallback: name-based regex match. This catches spaces whose
 * `category` field is missing / wrong (custom workflows imported
 * without category metadata, legacy data, etc.) — without it,
 * "TO DO" rows on those spaces would silently render as solid discs.
 * Matches the common English variants of pre-work status names.
 */
const UNSTARTED_NAME_RE =
  /^(to.?do|todo|backlog|not.?started|open|new|pending|to.?be.?done)$/i

export function isUnstartedStatus(statusRow, rawName) {
  if (statusRow?.category === 'not_started') return true
  if (statusRow?.category === 'backlog') return true
  const probe = String(statusRow?.name ?? rawName ?? '').trim()
  return UNSTARTED_NAME_RE.test(probe)
}

/**
 * Status indicator dot.
 *
 *   - `unstarted=true`  → DASHED ring (broken outline) — the ClickUp
 *     "pending / not yet picked up" affordance. `strokeDasharray=3 4`
 *     gives ~6 visible dash segments around the circle.
 *   - `unstarted=false` → BULLSEYE — thin halo ring + dominant filled
 *     centre disc. Distinguishes "actively being worked on" from a
 *     flat solid colour blob.
 *
 * `size` defaults to 14 (matches the previous h-3.5/w-3.5 CSS dot).
 * Pass 12 for compact-dropdown variants, 13 for subtask rows.
 */
export function StatusDot({ color, unstarted, size = 14 }) {
  const cx = 8
  const cy = 8
  if (!unstarted) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        aria-hidden="true"
        className="shrink-0 block"
      >
        <circle
          cx={cx}
          cy={cy}
          r={6.5}
          fill="none"
          stroke={color}
          strokeWidth={1.25}
        />
        <circle cx={cx} cy={cy} r={4} fill={color} />
      </svg>
    )
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="shrink-0 block"
    >
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeDasharray="3 4"
        strokeLinecap="round"
      />
    </svg>
  )
}
